import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  Receipt,
  CreditCard,
  Banknote,
  Percent,
  ShoppingBag,
  FileBarChart,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  FileText,
  ChevronDown,
  User,
  Key,
  Link,
  FileMinus,
  UtensilsCrossed,
  Clock,
  UsersRound,
  MessageSquare
} from 'lucide-react';
import FeedbackWidget from '../components/FeedbackWidget.jsx';

// Navigation items with page key mapping
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, pageKey: 'dashboard' },
  { name: 'Family Registration', href: '/families', icon: UsersRound, pageKey: 'families' },
  { name: 'Students', href: '/students', icon: GraduationCap, pageKey: 'students' },
  { name: 'Fee Structure', href: '/fee-structure', icon: Receipt, pageKey: 'fee_structure' },
  { name: 'Invoices', href: '/invoices', icon: FileText, pageKey: 'invoices' },
  { name: 'Payments', href: '/payments', icon: CreditCard, pageKey: 'payments' },
  { name: 'Credit Notes', href: '/credit-notes', icon: FileMinus, pageKey: 'credit_notes' },
  { name: 'Bank Reconciliation', href: '/bank-reconciliation', icon: Banknote, pageKey: 'bank_reconciliation' },
  { name: 'Discounts', href: '/discounts', icon: Percent, pageKey: 'discounts' },
  { name: 'Expenses', href: '/expenses', icon: ShoppingBag, pageKey: 'expenses' },
  { name: 'Uniforms', href: '/uniforms', icon: ShoppingBag, pageKey: 'uniforms' },
  { name: 'Lunch Coupons', href: '/lunch-coupons', icon: UtensilsCrossed, pageKey: 'lunch_coupons' },
  { name: 'Club Attendance', href: '/club-attendance', icon: Clock, pageKey: 'club_attendance' },
  { name: 'Reports', href: '/reports', icon: FileBarChart, pageKey: 'reports' },
];

const adminNavigation = [
  { name: 'Users', href: '/users', icon: Users, pageKey: 'users' },
  { name: 'Tally Setup', href: '/tally-setup', icon: Link, pageKey: 'tally_setup' },
  { name: 'Settings', href: '/settings', icon: Settings, pageKey: 'settings' },
  { name: 'Feedback Tickets', href: '/feedback-tickets', icon: MessageSquare, pageKey: 'feedback_tickets' },
];

const DashboardLayout = () => {
  const { user, logout, isAdmin, hasPageAccess, userBranches, roleName } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Filter navigation based on user permissions
  const filteredNavigation = navigation.filter(item => hasPageAccess(item.pageKey));
  // Administration items the user can access (admins see all via the 'all' page key)
  const filteredAdminNavigation = adminNavigation.filter(item => hasPageAccess(item.pageKey));

  const NavItem = ({ item }) => (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? 'active' : 'text-[#5C635F] hover:text-[#1C1F1D]'}`
      }
      onClick={() => setSidebarOpen(false)}
      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon className="w-5 h-5" />
      <span>{item.name}</span>
    </NavLink>
  );

  // Get branch display text
  const getBranchDisplay = () => {
    if (isAdmin) return 'All Branches';
    if (!userBranches || userBranches.length === 0) return 'No Branch';
    if (userBranches.length === 1) return userBranches[0];
    return `${userBranches.length} Branches`;
  };

  return (
    <div className="min-h-screen bg-[#F5F5FA]">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-[#D8D9E8] transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b border-[#D8D9E8]">
            <img src="https://acornsschools.com/wp-content/uploads/2024/06/Plain-Acorns-1536x587.png" alt="Acorns Schools" className="h-10 object-contain" />
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 hover:bg-[#ECEDF5] rounded">
              <X className="w-5 h-5 text-[#5C635F]" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {filteredNavigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
            {filteredAdminNavigation.length > 0 && (
              <div className="pt-4 mt-4 border-t border-[#D8D9E8]">
                <p className="px-3 text-xs font-semibold text-[#5C635F] uppercase tracking-wider mb-2">Administration</p>
                {filteredAdminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} />
                ))}
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-[#D8D9E8]">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9 bg-[#3E3F96]">
                <AvatarFallback className="bg-[#3E3F96] text-white text-sm">
                  {user?.name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1C1F1D] truncate">{user?.name}</p>
                <p className="text-xs text-[#5C635F]">{roleName}</p>
              </div>
            </div>
            {/* Branch badges */}
            {userBranches && userBranches.length > 0 && !isAdmin && (
              <div className="mt-2 flex flex-wrap gap-1">
                {userBranches.slice(0, 2).map(branch => (
                  <Badge key={branch} variant="outline" className="text-[10px] px-1.5 py-0">
                    {branch}
                  </Badge>
                ))}
                {userBranches.length > 2 && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    +{userBranches.length - 2}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-[#D8D9E8]">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-[#ECEDF5] rounded-lg" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-[#5C635F]" />
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <img src="https://acornsschools.com/wp-content/uploads/2024/06/Plain-Acorns-1536x587.png" alt="Acorns" className="h-8 object-contain" />
          </div>
          <div className="hidden lg:flex items-center gap-2 text-sm text-[#5C635F]">
            <Building2 className="w-4 h-4" />
            <span>{getBranchDisplay()}</span>
            {!isAdmin && userBranches && userBranches.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 px-2">
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Your Branches</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {userBranches.map(branch => (
                    <DropdownMenuItem key={branch}>
                      <Building2 className="w-4 h-4 mr-2" />
                      {branch}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8 bg-[#3E3F96]">
                  <AvatarFallback className="bg-[#3E3F96] text-white text-xs">{user?.name?.charAt(0) || 'A'}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm text-[#1C1F1D]">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-[#5C635F]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>
                <span className="block">{user?.name}</span>
                <span className="block text-xs font-normal text-[#5C635F]">{roleName}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                <User className="w-4 h-4 mr-2" />Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="change-password-menu-item">
                <Key className="w-4 h-4 mr-2" />Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-[#C85A40] focus:text-[#C85A40]" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* pb-24 keeps page content (pagination, footers) clear of the floating feedback launcher */}
        <main className="p-4 md:p-6 lg:p-8 pb-24 md:pb-24 lg:pb-24">
          <Outlet />
        </main>
      </div>

      {/* Floating voice/video feedback recorder — available to every signed-in user */}
      <FeedbackWidget />
    </div>
  );
};

export default DashboardLayout;
